
<script>
import { defineComponent, onMounted, ref, onBeforeUnmount, computed, inject } from 'vue'
import Scroller from '@kit/utils/Scroller'
import { animate } from '@kit/utils/Animate'
import Config from '@kit/utils/Config'
import { inBrowser } from '@kit/utils/EnvironmentHelper'
import { STYLE_STRING } from '@kit/utils/Themer'

/*
Look out for this.
# 161 Img problem with sb-g* classes.

This:
        <li v-for="flower in prefetch.availability" class="sb sb-h sb-alt2 sb-greedy-1 sb-align-cv">
          
          <Img class="sb sb-explicit sb-gr" width="100px" :theme="xLazyTheme_" :src="flower.flower_image[0].image.guid"/>

The problem here is that the Img is the first in the container. The sb-* classes don't apply the margin to the 
first element. *But it's not the first element, because there's a secret absolute element that acts as the 
intersection test for the lazy mechanism*. Currently the workaround is just to add a manual margin:0px style in 
those cases but that's stupid.     

*/

const defaultConfig = new Config({

  loader: {
    wheel: "Spinner-04s-200px",
    fromProject: false,
  },

  // Images are one of the hardest 'easy' things you'll ever encounter in web-design.
  // There must be 50 different styles that affect the layout and display, and 
  // to add to the mess, you have the issue of img vs background-img on a div.
  //
  // (E.g.: background-size vs object-fit and they're almost the same thing except background-size
  // can accept arbitary dimensions. Things like this makes theming an real nightmare)
  //
  // What I've found to be the case is that showing images on websites broadly breaks
  // down into respecting these catagories of use-cases:
  //
  // auto: You want to show the whole image and you're not concerned about size.
  //   In this case, what typically happens is that:
  //   >>> One dimension scales up automatically, the other is fixed.
  //   Position is settable. 
  //   Aspect is not settable.
  //   This is an img tag always.
  //  
  // fit: You have a size in mind and you want the image to fit in a certain way
  //   >>> The image fits to the area, contain or cover. <<< 
  //   Position is settable.
  //   Aspect is settable.
  //   Gutter is settable, but will override position and fit.
  //   This is img or div.
  //
  // region: You have a size in mind and you want to display a particular region of the image
  //   >>> Zoom in on a part and it will fill the area. <<<
  //   Aspect is settable.
  //   Position is not settable. 
  //   Fit is not settable. 
  //   This is always a div.
  //
  //
  display:'auto', //'fit', 'region'

  //width and height, pretty standard.
  width:'100%',
  height: null,

  //cover or contain. Good for both background and img styles.
  fit: 'cover',

  //Region display mode. The region. A string like this: {regionSize} {regionX} {regionY} or just null.
  region: null, 

  //Fit display mode. Integer. The percent of the gutter that you want around the image. 
  gutter: null,
  
  //Fit or region display mode. The aspect-ratio that you want.
  aspect: null,

  //does this act like a wrapper? If true, then we'll definitely not use an image.
  //It would be semantically silly to use an img tag as a background element anyway.
  wrapper: false, 

  //The tag to use if this isn't an img element.
  tag: 'div',

  //The aria label
  ariaLabel: null,
  ariaRole:"img",

})

// Notes:
//
// background-image: url('https://upload.wikimedia.org/wikipedia/commons/thumb/a/a4/Filmaspectratios.svg/800px-Filmaspectratios.svg.png');
// background-position: -40px 97%;
//    --weird thing. If you do px you have to do negative. With % it's positive.
//

const _scroller = inBrowser ? new Scroller() : null


export default defineComponent({
  name: "Img",
  props: {
    "src": { required: true },

    //The theme
    "theme": { required: false },

    //display properties
    "style": { required: false, type: String },
    "class": { required: false },
    "display": {required: false },
    "width": {required: false },
    "height": {required: false },
    "aspect": {required: false},
    "position": { required: false},
    "gutter": { required: false},
    "fit": {required: false },
    "region": { required: false },
    "wrapper": { required: false },
    "tag": { required: false },

    //aria
    "ariaLabel": { required: false },
    "ariaRole": { required: false },
    "ariaDescribedBy": { required: false },

    //The loading
    "ldisplay": { required: false, default: (props) => props.display },
    "lstyle": { required: false, type: String, default: (props) => props.style },
    "lclass": { required: false, default: (props) => props.class },
    "laspect": {requiref: false, default: (props) => props.aspect },
    "lgutter": {requiref: false, default: (props) => props.gutter },
    "lregion": { required: false, default: (props) => props.region },
    "lposition": { required: false, default: (props) => props.position },
    "lfit": { required: false, default: (props) => props.fit },
    "ltag": { required: false, default: (props) => props.tag },

    "debugLoader": {required: false}


  },

  setup(props, context) {

    const root = ref(null) 
    const loaded = ref(false) 

    let loaderURL = ""

    const themer = inject("themer")

    const { getProp } = themer({props, defaultConfig })

    const loadImg = async() => {
      await new Promise((resolve, reject) => {
        let img_ = new Image()
        img_.onload = () => {
          resolve()
        }
        img_.onerror = () => {
          reject()
        }
        img_.src = props.src
     
      })
    }

    const class_ = computed(() => {
      let l = (loaded.value && !props.debugLoader) ? '' : 'l'
      return getProp(l+"class")
    })

    const style_ = computed(() => {
      let l = (loaded.value && !props.debugLoader) ? '' : 'l'

      let width = getProp(l+"width")
      let height = getProp(l+"height")
      let fit = getProp(l+"fit")
      let aspect = getProp(l+"aspect")
      let style = getProp(l+"style", STYLE_STRING)
      let display = getProp(l+"display")
      let position = getProp(l+"position")
      let gutter = getProp(l+"gutter")

      const t = tag.value 

      //If there's an image, then we respect the width, height, aspect ratio
      //and the object-size:cover or contain.
      //If it's an image, then either either an 'auto' display, or it's fit.
      //If it's 'auto', aspect ratio will not be applied.
      if(t == 'img') {

        if(display == 'auto') {
          if(width && !height) {
            height = 'auto'
          } else 
          if(!width && height) {
            width = 'auto'
          }
          if(!width && !height) {
            width = '100%'
            height = '100%'
          }
        }

        const _a = display == 'fit' && aspect ? ` aspect-ratio:${aspect}; ` : ''
        const _f = display == 'fit' && fit && !gutter ? ` object-fit:${fit}; ` : ''
        const _f2 = display == 'fit' && gutter ? ` object-fit:cover; ` : ''
        const _f3 = display == 'auto' ? ' object-fit:contain; ' : ''
        const _p = display == 'fit' && position && !gutter ? ` object-position:${position}; ` : ''
        const _s = style ? ` ${style}` : ''

        //If there's a gutter, then we're going to apply a padding.
        const _g = display == 'fit' && gutter ? ` padding:${gutter}%` : ''

        if(display == 'auto') {
          if(aspect && width != 'auto' && height != 'auto') {
            throw new Error("You can't specify width, height AND an aspect ratio (display=auto)")
          }
        } else {
          if(aspect && width && height ) {
            throw new Error("You can't specify width, height AND an aspect ratio (display=fit)")
          }
        }

        return `width:${width}; height:${height};${_a}${_f}${_f2}${_f3}${_p}${_g}${_s};`
      } 
      
      //Else, it's a background image which means its quite a lot more flexible and we
      //have to respect the active region.
      //If div, that means that it's either a display 'fit' or 'region'
      else {

        //If it's a div, any only one dimension is specified,
        //then we're going to just assume a square aspect ratio.
        if(((width && !height) || (height && !width))) {
          if(!aspect) {
            aspect = '1'
          }
        }

        const region = region_.value

        const base = `background-repeat:no-repeat; background-image:url('${imgSrc_.value}'); `

        const _w = width ? ` width:${width};` : ''
        const _h = height ? ` height:${height};` : ''
        const _a = aspect ? ` aspect-ratio:${aspect}; ` : ''
        const _s = style ? ` ${style}` : ''

        //If the region is null, 
        if(display == 'fit') {
  
          //If there's a gutter, then calculate the background-size
          if(gutter) {
            const _b = `background-size:${100 - gutter*2}%; background-position:center; `
            return `${base}${_w}${_h}${_a}${_b}${_s};`
          } 
          
          //else, use the fit and position
          else {
            const _f = fit ? ` background-size:${fit}; ` : ''
            const _p = position ? ` background-position:${position}; ` : ''
            return `${base}${_w}${_h}${_a}${_f}${_p}${_s};`
          }

        } else 
        if(display == 'region') {

          // if(!region || region.length != 3) {
          //   throw new Error("Malformed region setting:  Must be three space-separated numbers: {regionSize} {x} {y}")
          // }

          if(region.length == 3) {
            const s = region[0]
            const x = region[1]
            const y = region[2]

            if(isNaN(s) || isNaN(x) || isNaN(y)) {
              throw new Error("Region: NaNs coming through: "+s+" "+x+" "+y)
            }
          
            //If it's three, then we're going to use the three numbers in concert 
            //to affect the viewable area that will fill the element.
            const _b = `background-size:${(100/s)*100}%; background-position:${x}% ${y}%;`

            return `${base}${_w}${_h}${_a}${_b}${_s};`
          } else 
          if(region.length == 4) {
            const s1 = region[0]
            const s2 = region[1]
            const x = region[2]
            const y = region[3]

            // if(isNaN(s) || isNaN(x) || isNaN(y)) {
            //   throw new Error("Region: NaNs coming through: "+s+" "+x+" "+y)
            // }
          
            //If it's three, then we're going to use the three numbers in concert 
            //to affect the viewable area that will fill the element.
            const _b = `background-size:${s1} ${s2}; background-position:${x}% ${y}%;`

            return `${base}${_w}${_h}${_a}${_b}${_s};` 
          }

        }

      }

    })  



    //The region of the image.
    //If the region is an list of numbers, then its an "active-region"
    //which is to say WE tell the image which part of it should be filling
    //the element.
    //If the region is just one number, then we take that to mean that we just 
    //want the image to scale down and be in the center, which is passive.
    const region_ = computed(() => {
      let l = (loaded.value && !props.debugLoader) ? '' : 'l'
      const r = getProp(l+"region")

      if(!r) {
        return null
      }

      if(r.constructor == String) {
        return r.split(" ") 
      } else {
        return null
      }
    })


    const tag = computed(() => {

      let l = (loaded.value && !props.debugLoader) ? '' : 'l'
      let display = getProp(l+"display")
      const gutter = getProp(l+"gutter")
      const wrapper = getProp('wrapper')
      const tag = getProp(l+'tag')

      if(gutter) {
        return getProp(l+"tag")
      } else 
      if(display == 'auto') {

        if(wrapper) {
          console.log("Warning! Can't have a wrapper with an img tag 683729: src="+getProp("src"))
        }
        return 'img'        
      }     
      else { 

        if(region_.value || wrapper || tag != 'img') {
          const tagType = getProp(l+"tag")
          return tagType
        } else {
          return 'img'
        }

      }

    })

    const enteredIntoView = async(info) => {
      if(!loaded.value && inBrowser) {
        if(!props.debugLoader) {
          await loadImg()
          const el = info.element
          loaded.value = true  
          context.emit("lazyImgLoaded")
          el.style.opacity = 0;
          animate({targets:el, opacity:1, easing:"linear", duration:300 })
        }
      }
    }

    const loaderSrc_ = computed(() => {
      const gif = getProp("loader.wheel")
      if(getProp('loader.fromProject')) {
        require(`@project/assets/images/spinners/${gif}.gif`);
      } else {
        require(`@kit/assets/images/spinners/${gif}.gif`);
      }
      loaderURL = `/assets/${gif}.gif`
      return loaderURL
    })


    const imgSrc_ = computed(() => {
      if(loaded.value && !props.debugLoader) {
        return props.src
      } else {
        return loaderSrc_.value
      }
    })

    onMounted(() => {
      //_scroller.register(root.value, {"type":"asyncAction", "debugStyle":"background:#FF0055; opacity:0.5;", "th":0.1, "dy":-100, "action":enteredIntoView})
      _scroller.register(root.value, {"type":"asyncAction", "th":0.1, "dy":-100, "action":enteredIntoView })
    })
    onBeforeUnmount(() => {
      if(_scroller) {
        _scroller.unregisterNode(root.value)
      }
    })

    
    return { root, imgSrc_, style_, tag, class_, region_, getProp, loaded, enteredIntoView }
  },
  emits: ["lazyImgLoaded"]
});
</script>

<style scoped>
  img {
    box-sizing: border-box;
  }
</style>

<template>
  <img v-if="tag=='img'" 
    :alt="getProp('ariaLabel')" 
    aria-live="polite"
    :aria-busy="loaded ? 'false' : 'true'"  
    :aria-role="getProp('ariaRole')"
    :aria-describedby="getProp('ariaDescribedBy')"
    :src="imgSrc_" 
    :style="style_" 
    :class="class_" 
    ref="root">
  <div v-else
    :aria-busy="loaded ? 'false' : 'true'" 
    :aria-label="getProp('ariaLabel')" 
    :aria-role="getProp('ariaRole')"
    :aria-describedby="getProp('ariaDescribedBy')"
    aria-live="polite" 
    :style="style_" 
    :class="class_" 
    ref="root">
      <slot></slot>
    </div>
</template>